import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Translate as TranslateIcon,
  PlayArrow as PlayIcon,
  Favorite as FavoriteIcon,
  Visibility as ViewIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchTextById,
  updateText,
  generateTextTranslations,
  fetchTextStatistics,
  deleteText,
} from "../store/slices/adminTextsSlice";
import { SUPPORTED_LANGUAGES } from "../utils/languages";
import { toSafeChipColor } from "../utils/safeChipColor";

import type { ChangeEvent } from "react";

const DIFFICULTY_LEVELS = [
  { value: "BEGINNER", label: "Beginner", color: "success" },
  { value: "ELEMENTARY", label: "Elementary", color: "info" },
  { value: "INTERMEDIATE", label: "Intermediate", color: "warning" },
  {
    value: "UPPER_INTERMEDIATE",
    label: "Upper Intermediate",
    color: "warning",
  },
  { value: "ADVANCED", label: "Advanced", color: "error" },
  { value: "PROFICIENT", label: "Proficient", color: "error" },
];

// Используем авторитетный источник поддерживаемых языков
const AVAILABLE_LANGUAGES = SUPPORTED_LANGUAGES.map((lang) => ({
  code: lang.code,
  name: lang.name,
}));

type DifficultyValue =
  | "BEGINNER"
  | "ELEMENTARY"
  | "INTERMEDIATE"
  | "UPPER_INTERMEDIATE"
  | "ADVANCED"
  | "PROFICIENT";

interface EditFormState {
  topic: string;
  difficulty: DifficultyValue; // corresponds to Text.level
  freeUserAccess: boolean;
  content: string;
}

interface UsageStatistics {
  playCount?: number;
  viewCount?: number;
  [key: string]: number | undefined;
}

const TextDetailPage: React.FC = () => {
  const { textId } = useParams<{ textId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedText, loading, error } = useAppSelector(
    (state) => state.adminTexts,
  );

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>({
    topic: "",
    difficulty: "INTERMEDIATE",
    freeUserAccess: false,
    content: "",
  });
  const [translationLanguages, setTranslationLanguages] = useState<string[]>(
    [],
  );
  const [statistics] = useState<UsageStatistics | null>(null); // reserved for future detailed stats integration
  // Local error handling removed for now (not displayed in UI) to satisfy lint rules

  useEffect(() => {
    if (textId) {
      dispatch(fetchTextById(textId));
      dispatch(fetchTextStatistics(textId));
    }
  }, [dispatch, textId]);

  useEffect(() => {
    if (selectedText) {
      setEditForm({
        topic: selectedText.topic || "",
        difficulty: selectedText.level as DifficultyValue,
        freeUserAccess: selectedText.freeUserAccess || false,
        content: selectedText.content,
      });
    }
  }, [selectedText]);

  const handleEdit = (): void => {
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!textId) return;
    // local error cleared (no local state currently)
    const action = await dispatch(
      updateText({
        id: textId,
        data: {
          topic: editForm.topic,
          level: editForm.difficulty,
          freeUserAccess: editForm.freeUserAccess,
          content: editForm.content,
        },
      }),
    );
    if (updateText.fulfilled.match(action)) {
      setEditDialogOpen(false);
    } else if (updateText.rejected.match(action)) {
      // Could surface local error via Snackbar in future
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!textId || !selectedText) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete the text "${selectedText.topic}"? This action cannot be undone.`,
    );

    if (confirmed) {
      const action = await dispatch(deleteText(textId));
      if (deleteText.fulfilled.match(action)) {
        navigate("/texts");
      } else if (deleteText.rejected.match(action)) {
        // Deletion error could be handled with global notification system
      }
    }
  };

  const handleGenerateTranslations = async (): Promise<void> => {
    if (!textId || translationLanguages.length === 0) return;
    // Clear local error placeholder
    const action = await dispatch(
      generateTextTranslations({
        textId,
        languages: translationLanguages,
      }),
    );
    if (generateTextTranslations.fulfilled.match(action)) {
      setTranslateDialogOpen(false);
      setTranslationLanguages([]);
      dispatch(fetchTextById(textId));
    } else if (generateTextTranslations.rejected.match(action)) {
      // Translation error could be surfaced globally later
    }
  };

  const getDifficultyInfo = (
    level: string,
  ): { value: string; label: string; color: string } =>
    DIFFICULTY_LEVELS.find((d) => d.value === level) || {
      value: level,
      label: level,
      color: "default",
    };

  const getLanguageName = (code: string): string =>
    AVAILABLE_LANGUAGES.find((l) => l.code === code)?.name ||
    code.toUpperCase();

  const missingTranslations = AVAILABLE_LANGUAGES.filter(
    (lang) =>
      lang.code !== selectedText?.language &&
      !selectedText?.translations?.some((t) => t.targetLanguage === lang.code),
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!selectedText) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Text not found
      </Alert>
    );
  }

  const difficultyInfo = getDifficultyInfo(selectedText.level);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate("/texts")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" flex={1}>
          Text Details
        </Typography>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          startIcon={<TranslateIcon />}
          onClick={() => setTranslateDialogOpen(true)}
          disabled={missingTranslations.length === 0}
        >
          Add Translations
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {selectedText.topic || "Untitled"}
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <Chip
                label={getLanguageName(selectedText.language)}
                color="primary"
                size="small"
              />
              <Chip
                label={difficultyInfo.label}
                color={toSafeChipColor(difficultyInfo.color)}
                size="small"
              />
              <Chip
                label={selectedText.freeUserAccess ? "Free Access" : "Premium"}
                color={selectedText.freeUserAccess ? "success" : "default"}
                size="small"
              />
            </Box>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {selectedText.content}
            </Typography>
          </Paper>

          {/* Translations */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Available Translations ({selectedText.translations?.length || 0})
            </Typography>
            {selectedText.translations &&
            selectedText.translations.length > 0 ? (
              <List>
                {selectedText.translations.map((translation) => (
                  <ListItem key={translation.id} divider>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <LanguageIcon fontSize="small" />
                          <Typography variant="subtitle1">
                            {getLanguageName(translation.targetLanguage)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            whiteSpace: "pre-wrap",
                            maxHeight: "100px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {translation.translation}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary">
                No translations available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Statistics */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Box textAlign="center">
                    <PlayIcon color="primary" />
                    <Typography variant="h4">
                      {statistics?.playCount ||
                        selectedText._count?.progress ||
                        0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Plays
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box textAlign="center">
                    <FavoriteIcon color="secondary" />
                    <Typography variant="h4">
                      {selectedText._count?.favorites || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Favorites
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box textAlign="center">
                    <ViewIcon color="action" />
                    <Typography variant="h4">
                      {statistics?.viewCount || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Views
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box textAlign="center">
                    <Typography variant="h4">
                      {selectedText._count?.voices || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Voice Variants
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadata
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Created:</strong>{" "}
                {new Date(selectedText.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Updated:</strong>{" "}
                {new Date(selectedText.updatedAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Author:</strong>{" "}
                {selectedText.author?.email || "System"}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Text Size:</strong> {selectedText.textSize}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Status:</strong>{" "}
                {selectedText.isApproved ? "Approved" : "Pending"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Text</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Title/Topic"
                value={editForm.topic}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEditForm({ ...editForm, topic: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                select
                label="Difficulty Level"
                value={editForm.difficulty}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEditForm({
                    ...editForm,
                    difficulty: e.target.value as DifficultyValue,
                  })
                }
              >
                {DIFFICULTY_LEVELS.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                select
                label="Access Type"
                value={editForm.freeUserAccess ? "free" : "premium"}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEditForm({
                    ...editForm,
                    freeUserAccess: e.target.value === "free",
                  })
                }
              >
                <MenuItem value="free">Free Access</MenuItem>
                <MenuItem value="premium">Premium Only</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Content"
                value={editForm.content}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEditForm({ ...editForm, content: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Translation Dialog */}
      <Dialog
        open={translateDialogOpen}
        onClose={() => setTranslateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Translations</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select languages to generate translations for:
          </Typography>
          <List>
            {missingTranslations.map((lang) => {
              const selected = translationLanguages.includes(lang.code);
              return (
                <ListItem
                  key={lang.code}
                  onClick={() => {
                    setTranslationLanguages((prev) =>
                      prev.includes(lang.code)
                        ? prev.filter((l) => l !== lang.code)
                        : [...prev, lang.code],
                    );
                  }}
                  secondaryAction={
                    <Chip
                      label={selected ? "Selected" : "Select"}
                      color={selected ? "primary" : "default"}
                      size="small"
                    />
                  }
                  sx={{ cursor: "pointer" }}
                >
                  <ListItemText primary={lang.name} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranslateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleGenerateTranslations}
            variant="contained"
            disabled={translationLanguages.length === 0}
          >
            Generate Translations ({translationLanguages.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TextDetailPage;
